import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import {
    AttachmentFullViewState,
    getSaveToCloudEstimatedCompleteTimeInMS,
    saveToCloud,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import AddToCalendarActionSource from 'owa-attachment-full-view/lib/types/ActionSource';
import { addToCalendar } from 'owa-attachment-full-view/lib/utils/ContextMenu/addCalendarEventsAttachmentContextItemBuilder';
import { isSmimeAttachmentType } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import getAttachmentFileType from 'owa-attachment-model-store/lib/utils/getAttachmentFileType';
import { downloadAttachmentFromUrl } from 'owa-attachment-user-operations';
import { lazyDownloadSmimeAttachment } from 'owa-smime';
import { trace } from 'owa-trace';
import { SurfaceAction } from '../../types/SurfaceAction';

export function performSurfaceAction(
    action: SurfaceAction,
    viewState: AttachmentFullViewState
): void {
    switch (action) {
        case SurfaceAction.addToCalendar:
            addToCalendar(viewState, AddToCalendarActionSource.SurfaceAction);
            break;
        case SurfaceAction.download:
            downloadAttachment(viewState);
            break;
        case SurfaceAction.saveToCloud:
            saveToCloudInternal(viewState);
            break;
        default:
            assertNever(action);
    }
}

// Note: we use document.body as the container element since inside of downloadAttachment it creates a container element that
// is either the parent of the active element, or document.body.
// The parent of the active element is inside the attachmentFullViewState, so the logic in downloadAttachment
// mocks a click on the attachment in this case, which triggers opening SxS/a second download, based on the type of attachment/permissions.
async function downloadAttachment(viewState: AttachmentFullViewState) {
    const attachmentModel = getAttachment(viewState.attachmentId);
    if (!attachmentModel) {
        trace.error('AttachmentSurfaceActionButton.downloadAttachment attachmentModel is null');
        return;
    }
    logUsage(
        'AttachmentDownload',
        {
            entryPoint: 'SurfaceAction',
            isCloudy: viewState.isCloudy,
            isCompose: !viewState.isReadOnly,
        },
        {
            cosmosOnlyData: JSON.stringify({
                size: attachmentModel.model.Size,
                fileType: getAttachmentFileType(attachmentModel),
                attachmentId: attachmentModel.model.AttachmentId?.Id,
            }),
        }
    );

    if (isSmimeAttachmentType(attachmentModel.model)) {
        (await lazyDownloadSmimeAttachment.import())(attachmentModel.model);
    } else {
        downloadAttachmentFromUrl(
            attachmentModel.download.url,
            viewState.isCloudy,
            document.body /* containerElement*/
        );
    }
}

function saveToCloudInternal(viewState: AttachmentFullViewState) {
    const attachmentModel = getAttachment(viewState.attachmentId);
    if (!attachmentModel) {
        trace.error('AttachmentSurfaceActionButton.saveToCloud attachmentModel is null');
        return;
    }
    logUsage(
        'AttachmentSaveToCloud',
        {
            entryPoint: 'SurfaceAction',
        },
        {
            cosmosOnlyData: JSON.stringify({
                size: attachmentModel.model.Size,
                fileType: getAttachmentFileType(attachmentModel),
                attachmentId: attachmentModel.model.AttachmentId?.Id,
            }),
            isCore: true,
        }
    );

    startFakeProgressIndicator(
        getSaveToCloudEstimatedCompleteTimeInMS(viewState.attachmentId),
        0.1,
        (completePercent: number) => {
            setActionCompletePercent(viewState, completePercent);
        },
        viewState.attachmentId
    );
    saveToCloud(
        viewState,
        (succeeded: boolean) => {
            stopFakeProgressIndicator(viewState.attachmentId, succeeded);
        },
        'SurfaceAction'
    );
}
